import { registerSW } from './registerSwHelper';
import { toast } from 'sonner';

export interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const DISMISS_STORAGE_KEY = 'trading_dashboard_pwa_install_dismissed_at';
const DISMISS_COOLDOWN_MS = 24 * 60 * 60 * 1000; // 24 hours

let updateSW: ((reloadPage?: boolean) => Promise<void>) | null = null;
let deferredInstallPrompt: BeforeInstallPromptEvent | null = null;
const installListeners = new Set<(canInstall: boolean) => void>();

// Register browser-level listeners immediately on script execution
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredInstallPrompt = e as BeforeInstallPromptEvent;
    notifyInstallListeners(true);
  });

  window.addEventListener('appinstalled', () => {
    deferredInstallPrompt = null;
    notifyInstallListeners(false);
    toast.success('Trading Terminal Installed', {
      description: 'You can now launch the terminal directly from your home screen or desktop.',
    });
  });
}

/**
 * Initialize Service Worker registration with user-controlled update prompt.
 */
export function initPwaService() {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
    return;
  }

  // Register service worker with prompt strategy
  updateSW = registerSW({
    immediate: true,
    onNeedRefresh() {
      toast.info('Dashboard Update Available', {
        description: 'A new version of the dashboard is ready. Reload when convenient to apply.',
        duration: Infinity,
        action: {
          label: 'Reload',
          onClick: () => {
            if (updateSW) {
              updateSW(true);
            } else {
              window.location.reload();
            }
          },
        },
        cancel: {
          label: 'Dismiss',
          onClick: () => {
            // Dismiss toast without updating
          },
        },
      });
    },
    onOfflineReady() {
      // Shell precached
    },
  });
}

function notifyInstallListeners(canInstall: boolean) {
  installListeners.forEach((listener) => {
    try {
      listener(canInstall);
    } catch {
      // Ignore listener errors
    }
  });
}

/**
 * Check if the native browser install prompt is currently stored and ready to trigger.
 */
export function isInstallPromptAvailable(): boolean {
  return !!deferredInstallPrompt;
}

/**
 * Trigger browser install dialog if available
 */
export async function promptPwaInstall(): Promise<boolean> {
  if (!deferredInstallPrompt) {
    return false;
  }
  try {
    await deferredInstallPrompt.prompt();
    const choice = await deferredInstallPrompt.userChoice;
    deferredInstallPrompt = null;
    notifyInstallListeners(false);
    return choice.outcome === 'accepted';
  } catch (err) {
    console.error('Failed to trigger PWA install prompt:', err);
    deferredInstallPrompt = null;
    notifyInstallListeners(false);
    return false;
  }
}

/**
 * Subscribe to installability changes
 */
export function subscribeInstallPrompt(listener: (canInstall: boolean) => void): () => void {
  installListeners.add(listener);
  listener(!!deferredInstallPrompt);
  return () => {
    installListeners.delete(listener);
  };
}

/**
 * Check if app is running in standalone PWA mode
 */
export function isStandaloneMode(): boolean {
  if (typeof window === 'undefined') return false;
  const nav = window.navigator as unknown as { standalone?: boolean };
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    nav.standalone === true ||
    document.referrer.includes('android-app://')
  );
}

/**
 * Check if user dismissed the install banner recently (within 24 hours)
 */
export function isInstallDismissed(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const raw = localStorage.getItem(DISMISS_STORAGE_KEY);
    if (!raw) return false;
    const dismissedAt = parseInt(raw, 10);
    if (isNaN(dismissedAt)) return false;
    return Date.now() - dismissedAt < DISMISS_COOLDOWN_MS;
  } catch {
    return false;
  }
}

/**
 * Record user dismissal of the install banner in local storage
 */
export function dismissInstallPrompt(): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(DISMISS_STORAGE_KEY, Date.now().toString());
  } catch {
    // Ignore localStorage access errors
  }
}

/**
 * Helper for testing: inject or clear mock beforeinstallprompt event
 */
export function setDeferredPromptForTesting(promptEvent: BeforeInstallPromptEvent | null): void {
  deferredInstallPrompt = promptEvent;
  notifyInstallListeners(!!promptEvent);
}
